import React, { useState, useEffect } from "react";
import {
	Button,
	TextField,
	Stack,
	Typography,
	List,
	ListItem,
	ListItemAvatar,
	Avatar,
	ListItemText,
	IconButton,
	Divider,
	Box
} from "@mui/material";
import { Edit as EditIcon, DeleteOutline, Add as AddIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
	listCatalogProducts,
	createProduct,
	updateProduct,
	deleteProduct
} from "../../services/wabaFeatures";

const EMPTY = { retailerId: "", name: "", price: "", description: "", imageUrl: "", url: "", conjunto: "" };

// Aba de GESTÃO do catálogo: lista, cria, edita e exclui produtos.
// Exige token de catálogo por conexão (o backend valida). Criar/editar leva ~15min
// para o produto ficar ENVIÁVEL (indexação Meta), mas aparece na lista na hora.
const WabaCatalogTab = ({ whatsappId }) => {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState(EMPTY);
	const [editingId, setEditingId] = useState(null); // null = criando

	const load = async () => {
		setLoading(true);
		try {
			const { data } = await listCatalogProducts(whatsappId);
			setProducts(data?.data?.products || []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (whatsappId) load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [whatsappId]);

	const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

	const startEdit = p => {
		setEditingId(p.id);
		setForm({
			retailerId: p.retailer_id || "",
			name: p.name || "",
			price: (p.price || "").toString().replace(/[^\d,.-]/g, ""),
			description: p.description || "",
			imageUrl: p.image_url || "",
			url: p.url || "",
			conjunto: p.product_type || ""
		});
	};

	const cancelEdit = () => {
		setEditingId(null);
		setForm(EMPTY);
	};

	const parsePriceCents = v => {
		const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
		return Number.isFinite(n) ? Math.round(n * 100) : NaN;
	};

	const handleSave = async () => {
		const priceCents = parsePriceCents(form.price);
		if (!form.name || !Number.isFinite(priceCents)) {
			toast.error("Informe nome e preço válido.");
			return;
		}
		if (!editingId && (!form.retailerId || !form.imageUrl)) {
			toast.error("Para criar, informe o código (retailer_id) e a URL da imagem.");
			return;
		}
		setSaving(true);
		try {
			if (editingId) {
				await updateProduct(whatsappId, editingId, {
					name: form.name,
					priceCents,
					description: form.description,
					imageUrl: form.imageUrl || undefined,
					url: form.url || undefined,
					productType: form.conjunto || ""
				});
				toast.success("Produto atualizado! (o envio reflete em ~15 min)");
			} else {
				await createProduct(whatsappId, {
					retailerId: form.retailerId,
					name: form.name,
					priceCents,
					description: form.description,
					imageUrl: form.imageUrl,
					url: form.url || undefined,
					productType: form.conjunto || undefined
				});
				toast.success("Produto criado! (fica enviável em ~15 min — indexação Meta)");
			}
			cancelEdit();
			await load();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async p => {
		if (!window.confirm(`Excluir "${p.name}"?`)) return;
		try {
			await deleteProduct(whatsappId, p.id);
			toast.success("Produto excluído.");
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Stack spacing={2}>
			<Typography variant="subtitle2">
				{editingId ? "Editar produto" : "Novo produto"}
			</Typography>
			<Stack spacing={1.5}>
				<TextField
					label="Código do produto (retailer_id)"
					value={form.retailerId}
					onChange={set("retailerId")}
					disabled={!!editingId}
					size="small"
					fullWidth
				/>
				<TextField label="Nome" value={form.name} onChange={set("name")} size="small" fullWidth />
				<TextField
					label="Preço (ex.: 199,90)"
					value={form.price}
					onChange={set("price")}
					size="small"
					fullWidth
				/>
				<TextField
					label="Descrição"
					value={form.description}
					onChange={set("description")}
					size="small"
					fullWidth
					multiline
					minRows={2}
				/>
				<TextField label="URL da imagem" value={form.imageUrl} onChange={set("imageUrl")} size="small" fullWidth />
				<TextField label="Link (opcional)" value={form.url} onChange={set("url")} size="small" fullWidth />
				<TextField
					label="Conjunto / categoria (ex.: Perfumaria)"
					value={form.conjunto}
					onChange={set("conjunto")}
					size="small"
					fullWidth
					helperText="Agrupa o produto; usado no envio para mandar o conjunto todo de uma vez."
				/>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						onClick={handleSave}
						variant="contained"
						size="small"
						startIcon={<AddIcon />}
						disabled={saving}
					>
						{saving ? "Salvando..." : editingId ? "Salvar" : "Adicionar"}
					</Button>
					{editingId && (
						<Button onClick={cancelEdit} size="small" disabled={saving}>
							Cancelar edição
						</Button>
					)}
				</Box>
			</Stack>

			<Divider />

			<Typography variant="subtitle2">Produtos do catálogo</Typography>
			{loading ? (
				<Typography variant="body2">Carregando…</Typography>
			) : products.length === 0 ? (
				<Typography variant="body2" color="textSecondary">
					Nenhum produto ainda.
				</Typography>
			) : (
				<List dense sx={{ maxHeight: 260, overflow: "auto" }}>
					{Object.entries(
						products.reduce((acc, p) => {
							const k = p.product_type || "Sem conjunto";
							(acc[k] = acc[k] || []).push(p);
							return acc;
						}, {})
					).map(([conjunto, itens]) => (
						<React.Fragment key={conjunto}>
							<ListItem sx={{ bgcolor: "action.hover", py: 0.5 }}>
								<ListItemText
									primaryTypographyProps={{ variant: "subtitle2" }}
									primary={`${conjunto} (${itens.length})`}
								/>
							</ListItem>
							{itens.map(p => (
								<ListItem
									key={p.id}
									secondaryAction={
										<>
											<IconButton edge="end" size="small" onClick={() => startEdit(p)}>
												<EditIcon fontSize="small" color="info" />
											</IconButton>
											<IconButton edge="end" size="small" onClick={() => handleDelete(p)}>
												<DeleteOutline fontSize="small" color="error" />
											</IconButton>
										</>
									}
								>
									<ListItemAvatar>
										<Avatar variant="rounded" src={p.image_url} alt={p.name} />
									</ListItemAvatar>
									<ListItemText primary={p.name} secondary={p.price} />
								</ListItem>
							))}
						</React.Fragment>
					))}
				</List>
			)}
		</Stack>
	);
};

export default WabaCatalogTab;
